import React, { useEffect } from "react";
import Loader from "../components/Loader";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import Newsletter from "../components/Newsletter";
import Breadcrumbs from "../components/Breadcrumbs";
import { useLocation } from "react-router-dom";
import StructuredData from "../seo/StructuredData";
import { breadcrumbJsonLd } from "../seo/jsonld";
import { DEFAULT_OG_IMAGE, SITE_NAME, SITE_URL } from "../seo/site";

export default function Privacy({ setHeader }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const pageLang = i18n?.language === "me" ? "me" : "en";
  const pageUrl = `${SITE_URL}${location.pathname}`;
  const pageTitle = `${t("privacy.title")} - ${SITE_NAME}`;
  const pageDescription = t("header.privacy.metaDescription");
  const enUrl = `${SITE_URL}/en/privacy-policy`;
  const meUrl = `${SITE_URL}/me/politika-privatnosti`;
  const breadcrumbItems = [
    { name: SITE_NAME, item: `${SITE_URL}/${pageLang}` },
    { name: t("privacy.title"), item: pageUrl },
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 1) {
        setHeader(true);
      } else {
        setHeader(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [setHeader]);

  return (
    <>
      <Helmet htmlAttributes={{ lang: pageLang === "me" ? "sr-ME" : "en" }}>
        {/* <!-- meta tag --> */}
        <meta charSet="utf-8" />
        <title>
          {pageTitle}
        </title>

        {/* <!-- Meta Tags--> */}
        <meta
          name="description"
          content={pageDescription}
        />
        <meta
          name="keywords"
          content="durō, duro, niche perfumery, niche parfimerija,  parfimerija, galerija"
        />
        <meta name="author" content="intellect.se" />
        <link rel="canonical" href={pageUrl} />
        <link rel="alternate" href={enUrl} hrefLang="en" />
        <link rel="alternate" href={meUrl} hrefLang="sr-ME" />
        <link rel="alternate" href={meUrl} hrefLang="x-default" />

        {/* <!-- responsive tag --> */}
        <meta http-equiv="x-ua-compatible" content="ie=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        {/* <!-- Facebook Metadata --> */}
        <meta property="og:type" content="website" />
        <meta property="og:image:height" content="1200" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      </Helmet>
      <Loader />
      {/* <!-- Main content Start --> */}
      <div className="main-content">
        {/* <!-- Breadcrumbs Start --> */}
        <div className="rs-breadcrumbs img3">
          <div className="container">
            <div className="breadcrumbs-inner">
              <h1 className="page-title">
                {t("privacy.title")}
                {/*<span className="watermark">{privacy.title}</span>*/}
              </h1>
            </div>
          </div>
        </div>
        <Breadcrumbs bg="light" />
        {/* <!-- Breadcrumbs End --> */}

        {/* <!-- Faq Section Start --> */}
        <div className="rs-faq gray-bg4 pt-120 md-pt-80">
          <div className="container">
            {/* <!-- Privacy policy Section Start --> */}
            <div className="rs-privacy-policy pt-10 md-pt-80">
              <div className="privacy-wrap pb-130 md-pb-90">
                <div className="sec-title mb-50">
                  <h2 className="title title2">{t("privacy.terms.title")}</h2>
                </div>
                <div className="privacy-content">
                  <p>{t("privacy.terms.list.main")}</p>
                  <ul className="check-list">
                    <li>{t("privacy.terms.list.li1")}</li>
                    <li>{t("privacy.terms.list.li2")}</li>
                    <li>{t("privacy.terms.list.li3")}</li>
                    <li>{t("privacy.terms.list.li4")}</li>
                  </ul>
                </div>
              </div>
              <div className="privacy-wrap pb-100 md-pb-90">
                <div className="sec-title mb-50">
                  <h2 className="title title2">
                    {t("privacy.policy.privacyPolicy.title")}
                  </h2>
                </div>
                <div className="privacy-content">
                  <p>{t("privacy.policy.privacyPolicy.li1")}</p>
                  <p>
                    {t("privacy.policy.privacyPolicy.contact")}{" "}
                    <a href="mailto:info@parfimerija-duro.me">
                      info@parfimerija-duro.me
                    </a>
                    .
                  </p>
                </div>
              </div>
              <div className="privacy-wrap pb-50 md-pb-50">
                <div className="sec-title mb-50">
                  <h2 className="title title2">
                    {t("privacy.policy.cookiePolicy.title")}
                  </h2>
                </div>
                <div className="privacy-content">
                  <p>{t("privacy.policy.cookiePolicy.p1")}</p>
                  <p>{t("privacy.policy.cookiePolicy.p2")}</p>
                  <p>{t("privacy.policy.cookiePolicy.p3.main")}</p>
                  <ul className="check-list">
                    <li>{t("privacy.policy.cookiePolicy.p3.li1")}</li>
                    <li>{t("privacy.policy.cookiePolicy.p3.li2")}</li>
                    <li>{t("privacy.policy.cookiePolicy.p3.li3")}</li>
                  </ul>
                  <p>
                    {t("privacy.policy.cookiePolicy.contact")}{" "}
                    <a href="mailto:info@parfimerija-duro.me">
                      info@parfimerija-duro.me
                    </a>
                    .
                  </p>
                </div>
              </div>
            </div>
            {/* <!-- Privacy policy Section End -->    */}
          </div>
          {/* <!-- Newsletter section start --> */}
          <div className="rs-newsletter style1 pt-90 md-pt-60 mb--65">
            <Newsletter />
          </div>
          {/* <!-- Newsletter section end --> */}
        </div>
        {/* <!-- Faq Section End --> */}
      </div>
      {/* <!-- Main content End --> */}
      <StructuredData data={breadcrumbJsonLd(breadcrumbItems)} />
    </>
  );
}
